import axios from 'axios';
import type { DomainInfo, AlternativeSuggestion, DomainCheckResult } from '../types/domain';

const API_BASE_URL: string = (import.meta.env.VITE_API_URL as string) || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Server response wrapper
interface ServerResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

// Domain info from server
interface DomainData {
  domain: string;
  available: boolean;
  tld: string;
  whoisData?: {
    registrar?: string;
    registrationDate?: string;
    expiryDate?: string;
  };
  checkedAt: string;
  error?: string;
  alternatives?: Array<{
    name: string;
    tld: string;
    available: boolean;
  }>;
}

// Re-export types for convenience
export type { DomainInfo, AlternativeSuggestion, DomainCheckResult };

export const checkDomain = async (domain: string): Promise<DomainCheckResult> => {
  try {
    const response = await api.post<ServerResponse<DomainData>>('/check', { domain });

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Error checking domain');
    }

    const serverData = response.data.data;

    // Transform server data to client format
    const domainInfo: DomainInfo = {
      name: serverData.domain.split('.')[0],
      available: serverData.available,
      tld: serverData.tld.startsWith('.') ? serverData.tld.substring(1) : serverData.tld,
      whoisData: serverData.whoisData,
    };

    return {
      domain: domainInfo,
      alternatives: serverData.alternatives || [],
    };
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      const serverError = error.response?.data as ServerResponse<unknown>;
      const msg = serverError?.message || 'Error checking domain';
      throw new Error(msg);
    }
    throw error;
  }
};

export interface DomainSuggestion {
  domain: string;
  available: boolean;
  registrar: string | null;
  expiryDate: string | null;
}

export const checkMultipleDomains = async (domains: string[]): Promise<DomainInfo[]> => {
  try {
    const response = await api.post<ServerResponse<DomainData[]>>('/check/batch', { domains });

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Error checking domains');
    }

    // Transform server data to client format
    return response.data.data.map(serverData => ({
      name: serverData.domain.split('.')[0],
      available: serverData.available,
      tld: serverData.tld.startsWith('.') ? serverData.tld.substring(1) : serverData.tld, // Remove leading dot
      whoisData: serverData.whoisData,
    }));
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      const serverError = error.response?.data as ServerResponse<unknown>;
      const msg = serverError?.message || 'Klaida tikrinant domenus';
      throw new Error(msg);
    }
    throw error;
  }
};

export const getDomainSuggestions = async (keyword: string): Promise<AlternativeSuggestion[]> => {
  try {
    const response = await api.post<ServerResponse<AlternativeSuggestion[]>>('/check/suggestions', {
      keyword,
    });

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Error getting domain suggestions');
    }

    return response.data.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      const serverError = error.response?.data as ServerResponse<unknown>;
      const msg = serverError?.message || 'Error getting domain suggestions';
      throw new Error(msg);
    }
    throw error;
  }
};

export default api;
